import {Component, Inject, Input, OnChanges, SimpleChanges} from '@angular/core';
import {isMultipleOf} from '@application/validators/is-multiple-of-validator';
import {FreeZone} from '@domain/production-schedule/free-zone/free-zone';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionSchedulePath} from '@domain/production-schedule/production-schedule-path';
import {ProductionSchedulePathsOfColoredYarnSet} from '@domain/production-schedule/production-schedule-paths-of-colored-yarn-set';
import {RestZone} from '@domain/production-schedule/rest-zone/rest-zone';
import {ZoneType} from '@domain/zone-type.enum';
import {PRODUCTION_SCHEDULES, ProductionSchedules} from '@infrastructure/http/production-schedule/production-schedules';
import {ProductionScheduleBuilderAspectRatio} from '@presentation/pages/texfab/production-schedule/add/plan/builder/production-schedule-builder-aspect-ratio';
import {Zone} from '@presentation/pages/texfab/production-schedule/add/plan/builder/zone';
import {PRODUCTION_SCHEDULE_PLAN, ProductionSchedulePlan} from '@presentation/pages/texfab/production-schedule/add/plan/production-schedule-plan';
import {AssertionUtils, BaseComponent, ZoneInputType, convertDentsToMillimeters} from '@vdw/angular-component-library';
import {cloneDeep} from 'lodash-es';
import {finalize, takeUntil} from 'rxjs/operators';

interface ZoneIndicatorPosition {
  left: number;
  right: number;
}

@Component({
  selector: 'app-production-schedule-zones',
  templateUrl: './production-schedule-zones.component.html',
  styleUrls: ['./production-schedule-zones.component.scss']
})
export class ProductionScheduleZonesComponent extends BaseComponent implements OnChanges {
  @Input() public productionScheduleVerifyWidth: number;
  @Input() public zones: Zone[];
  @Input() public suggestedZones: Zone[];
  @Input() public aspectRatio: ProductionScheduleBuilderAspectRatio;
  @Input() public productionSchedule: ProductionSchedule;

  public isVerifying = false;

  public readonly zoneIndicatorWidth = 8;
  public readonly zoneInputType = ZoneInputType;

  private zoneIndicatorPositions: ZoneIndicatorPosition[];

  public constructor(
    @Inject(PRODUCTION_SCHEDULE_PLAN) private readonly productionSchedulePlan: ProductionSchedulePlan,
    @Inject(PRODUCTION_SCHEDULES) private readonly productionSchedules: ProductionSchedules
  ) {
    super();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('zones' in changes || 'aspectRatio' in changes) {
      this.setZoneIndicatorsPosition();
    }
  }

  public getZoneIndicatorPosition(zoneIndex: number): number {
    return this.zoneIndicatorPositions[zoneIndex].left;
  }

  public isZonesInstanceOfRestZone(zone: Zone): boolean {
    return zone instanceof RestZone;
  }

  public isZonesInstanceOfFreeZone(zone: Zone): boolean {
    return zone instanceof FreeZone;
  }

  public getZoneInputType(zone: Zone): ZoneInputType {
    return zone instanceof RestZone ? ZoneInputType.RESTZONE : ZoneInputType.FREEZONE;
  }

  public canShowZoneIndicators(): boolean {
    return !AssertionUtils.isEmpty(this.zoneIndicatorPositions);
  }

  public getZoneWidthInMM(zone: Zone): number {
    return convertDentsToMillimeters({
      valueInDents: zone.technicalWidthInDents,
      factorForConversionFromMillimetersToDents: this.productionSchedule.machineQuality.reedDensity
    });
  }

  public maximumTechnicalValue(): number {
    let productionSchedulePathsWidthInDents = 0;
    this.productionSchedule.productionSchedulePathsOfColoredYarnSets
      .flatMap((path: ProductionSchedulePathsOfColoredYarnSet) => path.productionSchedulePaths)
      .forEach((path: ProductionSchedulePath) => (productionSchedulePathsWidthInDents += path.technicalWidthInDents));

    return this.productionSchedule.machine.dentsForFabric - productionSchedulePathsWidthInDents;
  }

  public onZoneValueChanged(value: number, zone: Zone): void {
    this.isVerifying = true;

    if (!this.isDifferenceBetweenOldAndNewTechnicalWidthInDentsValid(value, zone)) {
      value += 1;
    }

    this.productionSchedules
      .checkVerifyProductionSchedule(
        this.productionSchedule.id.toString(),
        this.getZoneType(zone),
        zone.startDent,
        this.getDifferenceBetweenOldAndNewTechnicalWidthInDents(value, zone),
        zone.coloredYarnSetPathNr
      )
      .pipe(
        finalize(() => {
          this.isVerifying = false;
        }),
        takeUntil(this.unSubscribeOnViewDestroy)
      )
      .subscribe((productionSchedule: ProductionSchedule) => {
        this.productionSchedule = ProductionSchedule.modifyProductionScheduleAfterVerify(this.productionSchedule, productionSchedule);
        this.productionSchedulePlan.dispatchNewProductionSchedule(cloneDeep(this.productionSchedule));
      });
  }

  private calculateZoneIndicatorPosition(zone: Zone): number {
    return zone.startDent + zone.technicalWidthInDents;
  }

  private setZoneIndicatorsPosition(): void {
    this.zoneIndicatorPositions = [];

    this.zones.forEach((zone: Zone) => {
      const left = this.calculateZoneIndicatorPosition(zone) * this.aspectRatio.width;
      const right = left + this.zoneIndicatorWidth * 2;
      this.zoneIndicatorPositions.push({left, right});
    });
  }

  private getZoneType(zone: Zone): ZoneType {
    let result: ZoneType;

    if (this.isZonesInstanceOfRestZone(zone)) {
      result = ZoneType.REST;
    } else if (this.isZonesInstanceOfFreeZone(zone)) {
      result = ZoneType.FREE;
    }

    return result;
  }

  private getDifferenceBetweenOldAndNewTechnicalWidthInDents(newValue: number, zone: Zone): number {
    return newValue - zone.technicalWidthInDents;
  }

  private isDifferenceBetweenOldAndNewTechnicalWidthInDentsValid(newValue: number, zone: Zone): boolean {
    let difference: number = this.getDifferenceBetweenOldAndNewTechnicalWidthInDents(newValue, zone);
    if (this.productionSchedule.machine.jacquard.amountOfPossiblePaths !== null && this.productionSchedule.machineQuality.weaveStructure.dentRepeat === 2) {
      return isMultipleOf(difference, this.productionSchedule.machineQuality.weaveStructure.dentRepeat);
    }
    return true;
  }
}
